import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  doc,
  updateDoc,
  setDoc,
  getDocs,
  Timestamp,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import { notificationService } from './notificationService';
import { readReceiptsService } from './readReceiptsService';
import { soundNotificationService, SoundType } from './soundNotificationService';

interface ChatNotificationData {
  chatId: string;
  lastReadTimestamp: Timestamp | null;
  unreadCount: number;
}

interface UserChatData {
  [chatId: string]: ChatNotificationData;
}

export class ChatNotificationService {
  private static instance: ChatNotificationService;
  private userUnreadData = new Map<string, UserChatData>();
  private activeListeners = new Map<string, () => void>();
  private markAsReadDebounce = new Map<string, NodeJS.Timeout>();

  public static getInstance(): ChatNotificationService {
    if (!ChatNotificationService.instance) {
      ChatNotificationService.instance = new ChatNotificationService();
    }
    return ChatNotificationService.instance;
  }

  private initializing = new Map<string, Promise<void>>();

  // Initialize chat notifications for a user. Auth, the sidebar and the chat bubble all call this
  // at sign-in, so concurrent calls share one setup instead of each attaching listeners.
  initializeChatNotifications(userEmail: string): Promise<void> {
    if (this.activeListeners.has(userEmail)) return Promise.resolve();
    const pending = this.initializing.get(userEmail);
    if (pending) return pending;
    const run = this.setUpChatNotifications(userEmail).finally(() => this.initializing.delete(userEmail));
    this.initializing.set(userEmail, run);
    return run;
  }

  private async setUpChatNotifications(userEmail: string): Promise<void> {
    try {
      // Load existing read timestamps from Firestore
      await this.loadExistingReadTimestamps(userEmail);
      
      // Get all chats for this user
      const chatsQuery = query(
        collection(db, 'chats'),
        where('participants', 'array-contains', userEmail.toLowerCase())
      );

      const unsubscribe = onSnapshot(chatsQuery, async (snapshot) => {
        const userChats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Set up message listeners for each chat
        for (const chat of userChats) {
          this.setupChatMessageListener(chat.id, userEmail, (chat as any).participants || []);
        }
      });

      this.activeListeners.set(userEmail, unsubscribe);
    } catch (error) {
      console.error('Error initializing chat notifications:', error);
    }
  }

  // Load existing read timestamps from Firestore and calculate initial unread counts
  private async loadExistingReadTimestamps(userEmail: string): Promise<void> {
    try {
      const userChatDataQuery = query(
        collection(db, 'userChatData'),
        where('userEmail', '==', userEmail)
      );
      
      const snapshot = await getDocs(userChatDataQuery);
      
      // Process each chat's read status
      for (const doc of snapshot.docs) {
        const data = doc.data();
        const chatId = data.chatId;
        
        if (chatId && data.lastReadTimestamp) {
          const chatData = this.getUserChatData(userEmail, chatId);
          chatData.lastReadTimestamp = data.lastReadTimestamp;
          
          // Calculate initial unread count by checking messages newer than last read timestamp
          await this.calculateInitialUnreadCount(userEmail, chatId, data.lastReadTimestamp);
          
          console.log(`Loaded existing read timestamp for chat ${chatId}:`, data.lastReadTimestamp, `Initial unread count: ${chatData.unreadCount}`);
        }
      }
    } catch (error) {
      console.error('Error loading existing read timestamps:', error);
    }
  }

  // Calculate initial unread count for a chat based on last read timestamp
  private async calculateInitialUnreadCount(userEmail: string, chatId: string, lastReadTimestamp: Timestamp): Promise<void> {
    try {
      const unreadMessagesQuery = query(
        collection(db, 'chats', chatId, 'messages'),
        where('timestamp', '>', lastReadTimestamp)
      );
      
      const unreadSnapshot = await getDocs(unreadMessagesQuery);
      const unreadCount = unreadSnapshot.docs.filter(
        d => d.data().sender !== userEmail
      ).length;
      
      // Set the initial unread count
      const chatData = this.getUserChatData(userEmail, chatId);
      chatData.unreadCount = unreadCount;
      
      console.log(`Chat ${chatId}: Found ${unreadCount} unread messages since last read timestamp`);
    } catch (error) {
      console.error(`Error calculating initial unread count for chat ${chatId}:`, error);
      // If we can't calculate, default to 0
      const chatData = this.getUserChatData(userEmail, chatId);
      chatData.unreadCount = 0;
    }
  }

  // Set up listener for messages in a specific chat
  private setupChatMessageListener(chatId: string, userEmail: string, participants: string[]): void {
    const listenerKey = `${userEmail}-${chatId}`;
    
    if (this.activeListeners.has(listenerKey)) {
      return; // Already listening to this chat
    }

    const messagesQuery = query(
      collection(db, 'chats', chatId, 'messages'),
      orderBy('timestamp', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(messagesQuery, async (snapshot) => {
      if (snapshot.empty) {
        console.log(`No messages in chat ${chatId} for user ${userEmail}`);
        return;
      }

      const latestMessage = snapshot.docs[0].data();
      const messageData = { id: snapshot.docs[0].id, ...latestMessage } as any;

      console.log(`Message listener fired for chat ${chatId}, message from: ${messageData.sender}, timestamp: ${messageData.timestamp?.seconds}`);

      // Skip if this is the user's own message
      if (messageData.sender === userEmail) {
        console.log(`Skipping own message in chat ${chatId}`);
        return;
      }

      // Check if this message has been read using read receipts
      const isMessageRead = await readReceiptsService.isMessageReadByUser(chatId, messageData.id, userEmail);
      
      if (isMessageRead) {
        console.log(`Skipping already read message in chat ${chatId}`);
        return;
      }

      console.log(`Creating notification for unread message in chat ${chatId}`);
      
      // Create notification for this user (deduplication handled by notification service)
      await this.createChatNotification(userEmail, chatId, messageData, participants);
      
      // Update unread count (we'll phase this out in favor of read receipts)
      this.incrementUnreadCount(userEmail, chatId);
      
      const updatedChatData = this.getUserChatData(userEmail, chatId);
      console.log(`Unread count incremented for chat ${chatId}, new count: ${updatedChatData.unreadCount}`);
    });

    this.activeListeners.set(listenerKey, unsubscribe);
  }

  // Create a chat notification
  private async createChatNotification(
    userEmail: string, 
    chatId: string, 
    messageData: any,
    participants: string[]
  ): Promise<void> {
    try {
      // Get sender display name
      const senderDisplayName = await this.getUserDisplayName(messageData.sender);
      
      const messagePreview = messageData.imageUrl 
        ? (messageData.text || '📷 Image') 
        : messageData.text;

      await notificationService.notifyFinChatMessage(
        userEmail,
        messageData.sender,
        senderDisplayName,
        messagePreview.substring(0, 100),
        chatId,
        messageData.id // Pass the message ID for deduplication
      );

      // Play chat message sound
      soundNotificationService.playSound(SoundType.Message);
    } catch (error) {
      console.error('Error creating chat notification:', error);
    }
  }

  // Get user display name
  private async getUserDisplayName(email: string): Promise<string> {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', email)
      );
      const snapshot = await getDocs(usersQuery);
      
      if (!snapshot.empty) {
        const userData = snapshot.docs[0].data();
        return userData.displayName || email;
      }
    } catch (error) {
      console.error('Error getting user display name:', error);
    }
    
    return email;
  }

  // Get or create user chat data
  private getUserChatData(userEmail: string, chatId: string): ChatNotificationData {
    if (!this.userUnreadData.has(userEmail)) {
      this.userUnreadData.set(userEmail, {});
    }

    const userData = this.userUnreadData.get(userEmail)!;
    if (!userData[chatId]) {
      userData[chatId] = {
        chatId,
        lastReadTimestamp: null,
        unreadCount: 0
      };
    }

    return userData[chatId];
  }

  // Increment unread count for a chat
  private incrementUnreadCount(userEmail: string, chatId: string): void {
    const chatData = this.getUserChatData(userEmail, chatId);
    chatData.unreadCount++;
  }

  // Mark chat as read with debouncing to prevent rapid calls
  async markChatAsRead(userEmail: string, chatId: string): Promise<void> {
    const debounceKey = `${userEmail}-${chatId}`;
    
    // Clear any existing debounce timer
    const existingTimer = this.markAsReadDebounce.get(debounceKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    
    // Set a new debounce timer
    const timer = setTimeout(async () => {
      await this.performMarkChatAsRead(userEmail, chatId);
      this.markAsReadDebounce.delete(debounceKey);
    }, 100); // 100ms debounce
    
    this.markAsReadDebounce.set(debounceKey, timer);
  }
  
  // Actual implementation of marking chat as read
  private async performMarkChatAsRead(userEmail: string, chatId: string): Promise<void> {
    console.log(`Marking chat as read: ${chatId} for user: ${userEmail}`);
    
    const chatData = this.getUserChatData(userEmail, chatId);
    const previousUnreadCount = chatData.unreadCount;
    
    // Only proceed if there are unread messages
    if (previousUnreadCount === 0) {
      console.log(`Chat ${chatId} already marked as read for user ${userEmail}`);
      return;
    }
    
    // Use current time as read timestamp to ensure all existing messages are marked as read
    // This prevents race conditions where the latest message timestamp might be equal to 
    // incoming message timestamps
    chatData.lastReadTimestamp = Timestamp.now();
    chatData.unreadCount = 0;
    
    console.log(`Chat read status updated: ${chatId}, previous unread: ${previousUnreadCount}, new unread: 0, read timestamp: ${chatData.lastReadTimestamp.seconds}`);

    // Also mark related notifications as read
    await this.markChatNotificationsAsRead(userEmail, chatId);

    // Trigger notification count refresh in the UI
    if (typeof window !== 'undefined' && (window as any).refreshNotificationCount) {
      setTimeout(() => {
        (window as any).refreshNotificationCount();
      }, 500); // Small delay to ensure Firestore updates are reflected
    }

    // Persist to Firestore for cross-device sync
    try {
      const userChatRef = doc(db, 'userChatData', `${userEmail}-${chatId}`);
      await setDoc(userChatRef, {
        chatId,
        userEmail,
        lastReadTimestamp: chatData.lastReadTimestamp,
        updatedAt: Timestamp.now()
      }, { merge: true });
      
      console.log(`Persisted read timestamp to Firestore: ${chatData.lastReadTimestamp.seconds}`);
    } catch (error) {
      console.error('Failed to persist chat read status:', error);
    }
  }

  // Mark all notifications for a specific chat as read
  private async markChatNotificationsAsRead(userEmail: string, chatId: string): Promise<void> {
    try {
      // Get all notifications for this user and chat
      const notificationsQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userEmail),
        where('data.chatId', '==', chatId),
        where('read', '==', false)
      );

      const snapshot = await getDocs(notificationsQuery);
      
      // Mark each notification as read
      const updatePromises = snapshot.docs.map(doc => 
        updateDoc(doc.ref, {
          read: true,
          updatedAt: new Date().toISOString()
        })
      );

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking chat notifications as read:', error);
    }
  }

  // Get unread count for a specific chat
  getUnreadCount(userEmail: string, chatId: string): number {
    const chatData = this.getUserChatData(userEmail, chatId);
    return chatData.unreadCount;
  }

  // Get unread count using read receipts (more accurate)
  async getUnreadCountWithReceipts(userEmail: string, chatId: string): Promise<number> {
    try {
      // Get all messages in the chat
      const messagesRef = collection(db, 'chats', chatId, 'messages');
      const messagesSnapshot = await getDocs(messagesRef);
      
      // Filter messages that are not from the current user
      const otherUsersMessages = messagesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(msg => msg.sender !== userEmail)
        .map(msg => msg.id);
      
      if (otherUsersMessages.length === 0) {
        return 0;
      }
      
      // Get unread count using read receipts service
      const unreadCount = await readReceiptsService.getUnreadCountInChat(
        chatId, 
        userEmail, 
        otherUsersMessages
      );
      
      return unreadCount;
    } catch (error) {
      console.error('Error getting unread count with receipts:', error);
      // Fallback to old method
      return this.getUnreadCount(userEmail, chatId);
    }
  }

  // Get total unread count for all chats
  getTotalUnreadCount(userEmail: string): number {
    if (!this.userUnreadData.has(userEmail)) return 0;
    
    const userData = this.userUnreadData.get(userEmail)!;
    return Object.values(userData).reduce((total, chat) => total + chat.unreadCount, 0);
  }

  // Force mark all chats as read for a user (useful for fixing stuck unread states)
  async markAllChatsAsRead(userEmail: string): Promise<void> {
    console.log(`Force marking all chats as read for user: ${userEmail}`);
    
    const userData = this.userUnreadData.get(userEmail);
    if (!userData) {
      console.log(`No chat data found for user ${userEmail}`);
      return;
    }

    const currentTime = Timestamp.now();
    const updatePromises: Promise<void>[] = [];

    // Update all chats to read status
    Object.keys(userData).forEach(chatId => {
      const chatData = userData[chatId];
      if (chatData.unreadCount > 0) {
        console.log(`Marking chat ${chatId} as read (was ${chatData.unreadCount} unread)`);
        chatData.lastReadTimestamp = currentTime;
        chatData.unreadCount = 0;

        // Persist to Firestore
        const userChatRef = doc(db, 'userChatData', `${userEmail}-${chatId}`);
        updatePromises.push(
          setDoc(userChatRef, {
            chatId,
            userEmail,
            lastReadTimestamp: currentTime,
            updatedAt: currentTime
          }, { merge: true })
        );

        // Also mark notifications as read for this chat
        updatePromises.push(this.markChatNotificationsAsRead(userEmail, chatId));
      }
    });

    // Mark all unread chat notifications as read
    try {
      const notificationsQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userEmail),
        where('type', '==', 'chat'),
        where('read', '==', false)
      );

      const snapshot = await getDocs(notificationsQuery);
      
      snapshot.docs.forEach(doc => {
        updatePromises.push(
          updateDoc(doc.ref, {
            read: true,
            updatedAt: new Date().toISOString()
          })
        );
      });
    } catch (error) {
      console.error('Error marking all chat notifications as read:', error);
    }

    // Wait for all updates to complete
    await Promise.all(updatePromises);
    console.log(`All chats marked as read for user ${userEmail}`);
  }

  // Clean up listeners when user logs out
  cleanup(userEmail: string): void {
    // Remove main chat listener
    const mainListener = this.activeListeners.get(userEmail);
    if (mainListener) {
      mainListener();
      this.activeListeners.delete(userEmail);
    }

    // Remove individual chat message listeners
    const listenersToRemove: string[] = [];
    this.activeListeners.forEach((_, key) => {
      if (key.startsWith(`${userEmail}-`)) {
        listenersToRemove.push(key);
      }
    });

    listenersToRemove.forEach(key => {
      const listener = this.activeListeners.get(key);
      if (listener) {
        listener();
        this.activeListeners.delete(key);
      }
    });

    // Clear any pending debounce timers for this user
    const timersToRemove: string[] = [];
    this.markAsReadDebounce.forEach((_, key) => {
      if (key.startsWith(`${userEmail}-`)) {
        timersToRemove.push(key);
      }
    });

    timersToRemove.forEach(key => {
      const timer = this.markAsReadDebounce.get(key);
      if (timer) {
        clearTimeout(timer);
        this.markAsReadDebounce.delete(key);
      }
    });

    // Clear user data
    this.userUnreadData.delete(userEmail);
  }
}

export const chatNotificationService = ChatNotificationService.getInstance(); 